import { Suspense, useEffect } from "react";
import { ThemeProvider, useTheme } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import NotFound from "./pages/NotFound";
import { useGlobalMentionsSync } from "./hooks/useGlobalMentionsSync";
import { useGlobalNotificationsSync } from "./hooks/useGlobalNotificationsSync";
import { useGlobalTeamsSync } from "./hooks/useGlobalTeamsSync";
import { useGlobalPipelinesSync } from "./hooks/useGlobalPipelinesSync";
import { useGlobalCustomFieldsSync } from "./hooks/useGlobalCustomFieldsSync";
import { useGlobalDepartmentAccessSync } from "./hooks/useDepartmentAccess";
import { useSessionCacheReset } from "./hooks/useSessionCacheReset";
import { useGlobalBacklogSync } from "./hooks/useGlobalBacklogSync";
import AppLayout from "./layouts/AppLayout";
import { TenantProvider, useTenant, useHostTenant } from "./hooks/useTenant";
import { APP_NAME } from "./lib/constants";
import { warmLazy } from "./lib/warmLazy";

// Secondary pages are code-split to keep the initial bundle small, through
// `warmLazy` (src/lib/warmLazy.tsx) rather than `React.lazy` directly: once the
// idle warm-up below has their modules, these render synchronously, so a first
// visit mounts the page exactly the way a tenth visit does. See that file for
// why prefetching alone does not achieve it.
const ROUTE_PAGES = {
  CreateAccount: warmLazy(() => import("./pages/CreateAccount"), "CreateAccount"),
  ResetPassword: warmLazy(() => import("./pages/ResetPassword"), "ResetPassword"),
  Directory: warmLazy(() => import("./pages/Directory"), "Directory"),
  Insights: warmLazy(() => import("./pages/Insights"), "Insights"),
  PersonDetail: warmLazy(() => import("./pages/PersonDetail"), "PersonDetail"),
  TeamDetail: warmLazy(() => import("./pages/TeamDetail"), "TeamDetail"),
  Profile: warmLazy(() => import("./pages/Profile"), "Profile"),
  NotificationSettings: warmLazy(() => import("./pages/NotificationSettings"), "NotificationSettings"),
  Unsubscribe: warmLazy(() => import("./pages/Unsubscribe"), "Unsubscribe"),
  Activity: warmLazy(() => import("./pages/Activity"), "Activity"),
  Mentions: warmLazy(() => import("./pages/Mentions"), "Mentions"),
  FocusList: warmLazy(() => import("./pages/FocusList"), "FocusList"),
  BacklogItemDetail: warmLazy(() => import("./pages/BacklogItemDetail"), "BacklogItemDetail"),
  Settings: warmLazy(() => import("./pages/Settings"), "Settings"),
};

const {
  CreateAccount,
  ResetPassword,
  Directory,
  Insights,
  PersonDetail,
  TeamDetail,
  Profile,
  NotificationSettings,
  Unsubscribe,
  Activity,
  Mentions,
  FocusList,
  BacklogItemDetail,
  Settings,
} = ROUTE_PAGES;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - data stays fresh longer
      gcTime: 24 * 60 * 60 * 1000, // 24 hours - keep data cached for a full day
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch when component mounts if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
  },
});

const PageFallback = () => (
  <div className="flex h-full items-center justify-center">
    <p className="text-muted-foreground">Loading...</p>
  </div>
);

/**
 * Warm every lazy route once the shell is idle.
 *
 * The first visit to a lazy page used to flash: React suspended, painted
 * `PageFallback`, then mounted the real page — and the page's entrance
 * (`.page-stagger`, `.page-rise`) began at that second mount, one frame after
 * the browser had already had the chance to paint the resolved tree. What the
 * eye caught was the finished page for a frame and then the animation starting
 * over. Every LATER visit was clean, which is the whole tell: by then the
 * component resolves synchronously. The fix is to make the first visit look
 * like a later one rather than to fight the animation, and `warmLazy` is what
 * makes a warmed module actually mean a synchronous mount.
 *
 * Idle, not eager: a prefetch that competes with the shell's own first paint
 * trades one stutter for another. `requestIdleCallback` carries a timeout so a
 * permanently busy tab still warms them, and the `setTimeout` branch covers
 * engines that do not implement it. Failures are swallowed on purpose — this is
 * an optimisation, and a chunk that will not preload is one React will fetch
 * again (and report properly) at navigation.
 */
const useIdleRoutePrefetch = () => {
  useEffect(() => {
    const warm = () => {
      for (const page of Object.values(ROUTE_PAGES)) void page.preload().catch(() => {});
    };
    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(warm, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(warm, 1200);
    return () => window.clearTimeout(timer);
  }, []);
};

// Keeps the browser tab titled after whichever tenant this request resolves to.
// On the apex (the default/landing host) the title follows the host's name even
// for a signed-in visitor, so the tab matches the landing they're looking at.
const TenantTitle = () => {
  const { name } = useTenant();
  const host = useHostTenant();
  // The default/"Welcome" tenant's name is already a complete label; every
  // real tenant's name is company-only, so the product name is appended here.
  const title = host.isDefault ? host.name : `${name} ${APP_NAME}`;
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
};

// The apex / any unmapped host resolves to the default "Welcome" tenant, which
// is a public landing page — the auth-gated app (and its sign-in modal) never
// mounts there, since that host has no accounts. Everything else is the app.
export const RootRoutes = () => {
  const { isDefault } = useHostTenant();

  // Host tenant not resolved yet — show neither surface rather than flash one.
  if (isDefault === null) return <PageFallback />;

  if (isDefault) {
    return (
      <Routes>
        {/* /login IS here, even though this host has no accounts of its own:
            identity is global, so the credential is the same one every
            workspace takes, and the server picks the workspace from the
            caller's own memberships. Without it, a person who does not know
            their company's address has no way in at all.

            The other two stay closed. An invitation and a reset both arrive as
            a link built for the workspace that issued them, so they land on
            that host with their token already in hand — reaching them here
            would mean arriving with nothing to act on. */}
        <Route path="/login" element={<Login />} />
        <Route path="/create-account" element={<Navigate to="/" replace />} />
        <Route path="/reset-password" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Dashboard />} />
        {/* One Home group in full — Home previews each group and hands off here. */}
        <Route path="/focus/:group" element={<FocusList />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/backlog" element={<Index />} />
        <Route path="/directory" element={<Directory />} />
        {/* People and Teams merged into Directory; the old list URLs redirect
            so bookmarks and in-app back-navigation keep working. Detail pages
            keep their original paths. */}
        <Route path="/people" element={<Navigate to="/directory" replace />} />
        <Route path="/people/:personId" element={<PersonDetail />} />
        <Route path="/teams" element={<Navigate to="/directory?tab=teams" replace />} />
        <Route path="/teams/:teamId" element={<TeamDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/notifications" element={<NotificationSettings />} />
        {/* The five configuration pages behind one tabbed destination; the
            old routes still resolve so existing links keep working. */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/statuses" element={<Navigate to="/settings?tab=statuses" replace />} />
        <Route path="/types" element={<Navigate to="/settings?tab=types" replace />} />
        <Route path="/priorities" element={<Navigate to="/settings?tab=priorities" replace />} />
        <Route path="/tenant" element={<Navigate to="/settings?tab=company" replace />} />
        <Route path="/departments" element={<Navigate to="/settings?tab=departments" replace />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/mentions" element={<Mentions />} />
        {/* /notifications merged into /mentions (Catch Up) */}
        {/* Recently deleted is now the trailing section of the backlog list —
            the old standalone page redirects into it. /unassigned redirects
            into the plain list for a different reason: it became a value in
            the Assigned column filter, and then that value went too, because
            a ticket here is assigned to someone when it is filed. */}
        <Route path="/recently-deleted" element={<Navigate to="/backlog" replace />} />
        <Route path="/item/:itemId" element={<BacklogItemDetail />} />
        <Route path="/unassigned" element={<Navigate to="/backlog" replace />} />
        {/* My work folded into Home (UX pass) — keep old links working. */}
        <Route path="/my-tickets" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/create-account" element={<CreateAccount />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* The link at the foot of every notification email. Outside AppLayout
          on purpose: it must work for someone who is not signed in, and the
          signed token in ?token= is its only authority. */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const AppContent = () => {
  const { theme } = useTheme();
  useGlobalMentionsSync();
  useGlobalNotificationsSync();
  useGlobalTeamsSync();
  useGlobalPipelinesSync();
  useGlobalCustomFieldsSync();
  useGlobalDepartmentAccessSync();
  useSessionCacheReset();
  useGlobalBacklogSync();
  useIdleRoutePrefetch();

  return (
    <TooltipProvider>
      <TenantTitle />
      <Sonner theme={theme as "light" | "dark" | "system"} />
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <RootRoutes />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  );
};

const App = () => (
  // "system" is the default: nobody has told us otherwise on a first visit,
  // and the OS setting is the best guess available. index.html applies the
  // matching class before this mounts so there is no light flash.
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <AppContent />
      </TenantProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
